import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

// ** history v4 only puts a key on entries it pushed itself, so the initial one needs a stand-in.
const INITIAL_ENTRY_KEY = '@@initial'

/**
 * "Back" that steps over query-string-only navigations.
 *
 * Filters kept in the URL push an entry on every change, so `history.goBack()` walks back through those
 * instead of leaving the page. This tracks the same-page entries added while the component is mounted and
 * jumps past all of them in a single `go()`.
 */
export function usePageBack() {
  const history = useHistory()

  // ** A link opened directly has no in-app entry to return to, so "back" would land on the browser's blank
  // ** page. Checked once on mount: everything tracked below is pushed later.
  const [canGoBack] = useState(() => window.history.length > 1 && Boolean(history.location.key))

  const pathnameRef = useRef(history.location.pathname)
  // ** Keys of the current page's entries in stack order, plus where inside that stack we stand.
  const stackRef = useRef([history.location.key || INITIAL_ENTRY_KEY])
  const indexRef = useRef(0)

  useEffect(
    () =>
      history.listen((location, action) => {
        const key = location.key || INITIAL_ENTRY_KEY

        // ** Left the page without unmounting (nested route): the old stack says nothing about the new one.
        if (location.pathname !== pathnameRef.current) {
          pathnameRef.current = location.pathname
          stackRef.current = [key]
          indexRef.current = 0
          return
        }

        if (action === 'PUSH') {
          // ** A push drops whatever was ahead of us, same as the browser's own stack.
          stackRef.current = stackRef.current.slice(0, indexRef.current + 1).concat(key)
          indexRef.current = stackRef.current.length - 1
        } else if (action === 'REPLACE') {
          stackRef.current[indexRef.current] = key
        } else {
          // ** POP covers both back and forward, so locate the entry by key rather than guess a direction.
          const nextIndex = stackRef.current.indexOf(key)
          indexRef.current = nextIndex === -1 ? 0 : nextIndex
        }
      }),
    [history]
  )

  // ** +1 for the step that actually leaves the page.
  const goBack = useCallback(() => history.go(-(indexRef.current + 1)), [history])

  return { canGoBack, goBack }
}
